import { KeyAnyValue } from "@/store/types";
import format from "date-fns/format";
import formatDistanceToNow from "date-fns/formatDistanceToNow";
import {
  backend,
  previewParams,
  thumbParams,
  featuredParams,
} from "../.config";
import {
  validDateString,
  validUrl,
  notEmptyString,
  isNumeric,
  emptyString,
} from "./validators";

export const extractSeqIntFromGuid = (str) => parseInt(str.substring(18), 16);

export const compareModTs = () => {
  if (Object.keys(backend).includes("checkMod")) {
    return backend.checkMod === true;
  } else {
    return false;
  }
};

export const extractId = (obj) => {
  let id = "";
  if (obj instanceof Object) {
    if (obj._id) {
      id = obj._id;
    }
  }
  return id;
};

export const hasObjectId = (obj) => {
  return extractId(obj).length > 3;
};

export const extractSeq = (obj) => {
  let id = -1;
  const guid = extractId(obj);
  if (guid.length > 18) {
    id = extractSeqIntFromGuid(guid);
  }
  return id;
};

export const extractDisplayId = (user, settings) => {
  const parts = [];
  if (user instanceof Object && settings instanceof Object) {
    if (user.uid) {
      parts.push(user.uid);
    }
    if (settings.submissions_deadline) {
      parts.push(settings.submissions_deadline.split("-").shift());
    }
  }
  return parts.join(" / ");
};

export const ucfirst = (str) => {
  if (notEmptyString(str)) {
    str = str.trim();
    return str.charAt(0).toUpperCase() + str.substring(1);
  }
};

export const capitalize = (str) => {
  if (notEmptyString(str)) {
    return str
      .split(" ")
      .map((w) => ucfirst(w))
      .join(" ");
  }
};

export const matchInt = (param: any, defaultValue = 0): number => {
  if (!isNumeric(param)) {
    return defaultValue;
  } else {
    return typeof param === "number" ? param : parseInt(param);
  }
};

export const pluralize = (num = 0, singular = "", plural = "s") => {
  let str = "";
  if (num === 1) {
    str = 1 + " " + singular;
  } else {
    if (singular.length > 2) {
      switch (plural) {
        case "s":
        case "es":
          str = num + " " + singular + plural;
          break;
      }
    } else {
      if (notEmptyString(plural)) {
        str = num + " " + plural;
      } else {
        str = num + " " + singular;
      }
    }
  }
  return str;
};

export const formatMinSecs = (secsInt = 0) => {
  const mins = Math.floor(secsInt / 60);
  const secs = Math.floor((secsInt % 60) / 15) * 15;
  const minWord = pluralize(mins, "minute");
  const secWord = pluralize(secs, "second");
  const parts = [];
  if (mins > 0) {
    parts.push(minWord);
  }
  if (secs > 0) {
    parts.push(secWord);
  }
  return secsInt >= 240 ? minWord : parts.join(" and ");
};

export const formatDate = (datetime = null, fmt = "") => {
  if (datetime) {
    if (emptyString(fmt)) {
      fmt = "dd/MM/yyyy";
    }
    return format(new Date(datetime), fmt);
  }
};

export const toRelativeTime = (datetime) => {
  if (datetime) {
    return formatDistanceToNow(new Date(datetime), {
      includeSeconds: false,
    });
  }
};

export const secsToTime = (secs: number) => {
  const parts = [];
  if (secs > 0) {
    const mins = Math.floor(secs / 60);
    if (mins > 0) {
      parts.push(mins + "m");
    }
    const s = secs % 60;
    if (s > 0) {
      parts.push(Math.round(s) + "s");
    }
  }
  return parts.join(" ");
};

export const dateToYear = (date: Date | string) => {
  let year = 0;
  if (typeof date === "string") {
    const yearStr = date.split("-").shift();
    if (isNumeric(yearStr)) {
      year = parseInt(yearStr, 10);
    }
  } else if (date instanceof Date) {
    year = date.getFullYear();
  }
  return year;
};

export const yearToDate = (year) => {
  if (!isNumeric(year)) {
    year = 0;
  }
  return new Date(year + "-07-01T00:00:00");
};

export const dateToDT = (date: string) => {
  if (validDateString(date, false)) {
    return new Date(date + "T00:00:00");
  }
};

export const dtToDate = (dt: Date | string) => {
  const isStr = typeof dt === "string";
  let valid = isStr;
  if (!isStr && dt instanceof Date) {
    dt = dt.toISOString();
    valid = true;
  }
  if (valid && typeof dt === "string") {
    return dt.split("T").shift();
  }
};

export const filterStateItems = (state, section, struct) => {
  const data: any = {};
  const keys = Object.keys(section);
  if (
    keys.includes(section) &&
    state[section] instanceof Object &&
    struct instanceof Object
  ) {
    Object.keys(state[section]).forEach((key) => {
      if (struct[key]) {
        data[key] = struct[key];
      }
    });
  }
  return data;
};

export const filterState = (state, struct) => {
  const data: any = {};
  if (state instanceof Object && struct instanceof Object) {
    Object.keys(state).forEach((key) => {
      if (struct[key]) {
        data[key] = struct[key];
      }
    });
  }
  return data;
};

export const matchDropdownOptions = (opts, key) => {
  let text = "";
  const opt = opts.find((op) => op.key === key);
  if (opt) {
    text = opt.text;
  }
  return text;
};

export const extractHash = () => {
  let hash = "";
  if (window.location.hash) {
    hash = window.location.hash.replace(/^#/, "");
  }
  return hash;
};

export const matchSizeParams = (sizeRef = "") => {
  switch (sizeRef) {
    case "thumb":
      return thumbParams;
    case "preview":
      return previewParams;
    case "featured":
      return featuredParams;
    default:
      return {
        mode: "",
        width: 0,
        height: 0,
      };
  }
};

export const applyImageSize = (filename: string, sizeRef = "") => {
  const params = matchSizeParams(sizeRef);
  if (params.mode.length > 0) {
    const parts = filename.split(".");
    const { width, height } = params;
    if (width > 0 && height > 0 && parts.length > 1) {
      const ext = parts.pop();
      const base = parts.join(".");
      return (
        [base, params.mode, [width, height].join("-")].join("-") + "." + ext
      );
    }
  }
  return filename;
};

export const insertImageSize = (filename: string, sizeRef = "") => {
  const fileRef =
    filename.indexOf("-resize-") > 4
      ? filename.replace(/-resize-(\d+)-(\d+)\./, ".")
      : filename;
  return applyImageSize(fileRef, sizeRef);
};

export const expandPath = (filename: string, sizeRef = "") => {
  let fileRef = "";
  if (typeof filename === "string" && filename.length > 3) {
    const ext = filename.split(".").pop();
    switch (ext) {
      case "jpg":
      case "jpeg":
      case "png":
      case "webp":
        fileRef = insertImageSize(filename, sizeRef);
        break;
      default:
        fileRef = filename;
    }
    return backend.mediaPath + fileRef;
  }
};

export const transformCloudinaryImagePath = (
  filename: string,
  sizeRef = ""
): string => {
  const params = matchSizeParams(sizeRef);
  const wPart = ["w", params.width].join("_");
  const hPart = ["h", params.height].join("_");
  const mode = params.mode === "resize" ? "limit" : params.mode;
  const modePart = ["c", mode].join("_");
  const sizePart = [wPart, hPart, modePart].join(",");
  return filename.replace(/\/upload\/[^/]+?\//, `/upload/${sizePart}/`);
};

export const expandCloudinary = (filename: string, sizeRef = ""): string => {
  if (typeof filename === "string" && filename.length > 3) {
    switch (sizeRef) {
      case "pdf":
      case "video":
        return filename;
      default:
        return transformCloudinaryImagePath(filename, sizeRef);
    }
  } else {
    return "";
  }
};

export const formatFileSize = (bytes) => {
  if (isNumeric(bytes)) {
    bytes = parseInt(bytes);
    if (bytes < 1024) {
      return bytes + " bytes";
    } else if (bytes < 1024 * 20) {
      return (bytes / 1024).toFixed(1).replace(/\.0$/, "") + "KB";
    } else if (bytes < 1024 * 1024) {
      return (bytes / 1024).toFixed(0) + "KB";
    } else if (bytes < 20 * 1024 * 1024) {
      return (bytes / (1024 * 1024)).toFixed(1).replace(/\.0$/, "") + "MB";
    } else {
      return (bytes / (1024 * 1024)).toFixed(0) + "MB";
    }
  }
};

export const formatPrice = (price, currency) => {
  let str = "";
  if (isNumeric(price)) {
    const flVal = parseFloat(price);
    let symbol = "";
    switch (currency) {
      case "USD":
        symbol = "$";
        break;
      case "EUR":
        symbol = "€";
        break;
      default:
        symbol = "£";
        break;
    }
    str = symbol + flVal.toFixed(2).replace(/\.00$/, "");
  }
  return str;
};

export const buildClassNames = (classNames, conditions) => {
  const isArray = classNames instanceof Array;
  const isString = isArray ? false : typeof classNames === "string";
  let cls = isArray ? classNames : isString ? [classNames] : [];
  if (conditions instanceof Object) {
    cls = cls.concat(
      Object.entries(conditions)
        .filter((entry) => entry[1] === true)
        .map((entry) => entry[0])
    );
  }
  return cls.join(" ");
};

export const calcNumWords = (str) => {
  let num = 0;
  if (typeof str === "string") {
    const wordRgx = new RegExp("([a-z0-9][a-z0-9]+|(a|A|I))");
    const skipCharsRgx = new RegExp("[^a-zA-Z0-9]", "i");
    const words = str
      .split(" ")
      .map((w) => w.replace(skipCharsRgx, ""))
      .filter((w) => wordRgx.test(w));
    num = words.length;
  }
  return num;
};

export const extractValidationType = (medium, validationOptions) => {
  const item = validationOptions.find((opt) => opt.key === medium);
  let str = "none";
  if (item) {
    if (item.value) {
      str = item.value;
    }
  }
  return str;
};

export const simplifyIntlLetters = (str: string): string => {
  const replMap = [
    ["a", "å", "á", "à", "ä", "ã", "â"],
    ["e", "é", "è", "ë", "ê"],
    ["i", "í", "ì", "ï", "î"],
    ["o", "ó", "ò", "ö", "ô", "õ"],
    ["u", "ú", "ù", "ü"],
    ["ny", "ñ"],
    ["ss", "ß"],
    ["s", "ś", "ŝ", "Ş", "ṣ"],
    ["c", "ç", "ć", "č"],
    ["d", "ð"],
  ];
  const letters = replMap.reduce((a, b) => a.concat(b.slice(1)), []);
  const allLetters = new RegExp("[^a-z0-9" + letters.join("") + "]", "i");
  str = str.trim().replace(allLetters, ".*?");
  replMap.forEach((row) => {
    if (row.length > 1) {
      const sourceSet = row.slice(1).join("");
      const sourcePat = new RegExp(`[${sourceSet}]`, "gi");
      const repl = row[0];
      str = str.replace(sourcePat, repl);
    }
  });
  return str;
};

export const sanitize = (str = "", max = 64) => {
  let out = "";
  if (typeof str === "string") {
    out = simplifyIntlLetters(str.trim().toLowerCase())
      .replace(/[^a-z0-9-]+/g, "-")
      .replace(/-+$/, "")
      .replace(/^-+/, "");
    if (out.length > max) {
      const parts = out.split("-");
      let sLen = 0;
      let stopIndex = parts.length - 1;
      for (let i = 0; i < parts.length; i++) {
        sLen += parts[i].length;
        if (sLen > max) {
          stopIndex = i;
          break;
        }
      }
      out = parts.slice(0, stopIndex).join("-");
    }
  }
  return out;
};

export const filterStatement = (statement) => {
  let valid = false;
  if (statement instanceof Object) {
    valid =
      (notEmptyString(statement.text, 5) ||
        notEmptyString(statement.image, 5)) &&
      notEmptyString(statement.type, 3);
  }
  return valid;
};

export const filterLink = (link) => {
  let valid = false;
  if (link instanceof Object) {
    valid = validUrl(link.url);
  }
  return valid;
};

export const filterContact = (contact) => {
  let valid = false;
  if (contact instanceof Object) {
    valid = notEmptyString(contact.identifier);
  }
  return valid;
};

export const correctUrl = (url) => {
  let corrected = url;
  if (notEmptyString(url, 3)) {
    if (!/^\w+:\/\//.test(url)) {
      corrected = "https://" + url;
    }
  }
  return corrected;
};

export const generateImageStyle = (filename, params) => {
  if (notEmptyString(filename) && params instanceof Object) {
    const { mode, width, height } = params;
    if (mode) {
      const parts = filename.split(".");
      const ext = parts.pop();
      return [parts.join("."), mode, width, height].join("-") + "." + ext;
    }
  }
  return filename;
};

export const moveInArray = (arrList, sourceIndex, newIndex) => {
  if (arrList instanceof Array) {
    if (isNumeric(sourceIndex) && sourceIndex >= 0 && newIndex >= 0) {
      const ws = arrList.splice(sourceIndex, 1);
      arrList.splice(newIndex, 0, ws[0]);
    }
  }
};

export const buildStatementImageUri = (imagePath = "") => {
  const randNum = Math.floor(Math.random() * 10000);
  const ts = (new Date().getTime() % 90000) + randNum;
  const randStr = "?t=" + ts;
  const { width, height } = thumbParams;
  const imgUri = imagePath.replace(
    /resize-\d+-\d+\./,
    ["resize", width, height].join("-") + "."
  );
  return [backend.mediaPath, imgUri].join("") + randStr;
};

export const blankImage = () => {
  return [backend.mediaPath, "blank.png"].join("");
};

export const cleanForWysiwyg = (text: string) => {
  return text
    .replace(/<\/?span[^>]*?>/g, "")
    .replace(/<br[^>]*?>/gi, "<code>&nbsp;</code>");
};

export const cleanFromWysiwyg = (text: string) => {
  return text.replace(/<code[^>]*?>[^<]*?<\/code>/gi, "<br>");
};

export const plainTextData = (
  text: string
): { chars: number; text: string; words: number } => {
  const plainText = notEmptyString(text)
    ? text
        .replace(/<\/?\w+[^>]*?>/g, " ")
        .replace(/\s\s+/, " ")
        .trim()
    : "";
  const chars = plainText.length;
  const words = chars > 0 ? plainText.split(/\s+/).length : 0;
  return { chars, words, text: plainText };
};

export const plainTextLength = (text: string): number => {
  return plainTextData(text).chars;
};

export function getImageSize(file): Promise<any> {
  const img = new Image();
  const objectUrl = window.URL.createObjectURL(file);
  const promise = new Promise((resolve, reject) => {
    img.onload = () => {
      // Natural size is the actual image size regardless of rendering.
      // The 'normal' `width`/`height` are for the **rendered** size.
      const width = img.naturalWidth;
      const height = img.naturalHeight;
      // Resolve promise with the width and height
      resolve({ width, height });
    };
    // Reject promise on error
    img.onerror = reject;
  });
  // Setting the source makes it start downloading and eventually call `onload`
  img.src = objectUrl;
  return promise;
}

export const toDisplayOptions = (settings: KeyAnyValue[] = []): any => {
  const entries = settings.map((item) => [item.key, item.value]);
  return Object.fromEntries(entries);
};

export const matchMode = () => {
  const { mode } = backend;
  return notEmptyString(mode) && ["student", "group"].includes(mode)
    ? mode
    : "student";
};

export const expandUrl = (url: string): string => {
  if (notEmptyString(url)) {
    url = url.trim();
    if (/^https?:\/?\/?$/.test(url)) {
      url = "";
    } else if (
      !/^https?:\/?\/?/.test(url) &&
      /^\w+[a-z0-9._-]*?\.\w+[a-z0-9._-]*?(\/[^ ]*?)?(#.*?)?(\?.*?)?$/i.test(
        url
      )
    ) {
      url = ["https:", url].join("//");
    }
    return url;
  } else {
    return "";
  }
};
